import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { format } from "date-fns";
import * as LucideIcons from "lucide-react";
import { Fingerprint, PenLine, TriangleAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/utils/cn";

import { formatPerson, formatTime } from "@/utils/helper";
import { deviceMeta } from "./attendance-summary/deviceMeta";

// Renders any Lucide icon by its PascalCase name string (e.g. "Utensils")
const DynamicIcon = ({
  name,
  size = 12,
  className,
  style,
}: {
  name: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const Icon = (
    LucideIcons as unknown as Record<string, LucideIcon | undefined>
  )[name];
  if (!Icon) return null;
  return <Icon size={size} className={className} style={style} />;
};
import {
  Tooltip as RadixTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  PendingOverlaySegment,
  PunchSource,
  TimelineResult,
  TimelineSegment,
} from "@/utils/buildTimelineSegments";

// ── Types ─────────────────────────────────────────────────────────────────────

interface AttendanceTimelineProps {
  result: TimelineResult;
  compact?: boolean;
  /** Renders only the thin bar at minimal height — no icons, markers, or badges. */
  barOnly?: boolean;
  isWeekend?: boolean;
  isAbsent?: boolean;
  className?: string;
  /** Pending-exception windows, rendered as a striped overlay on top of the
   * base timeline rather than replacing any of its segments. */
  pendingOverlaySegments?: PendingOverlaySegment[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Tooltip Start/End need seconds — a quick activity or unaccounted gap can
// be logged only a few seconds long (or, floored to 00:00, the SAME minute
// both ways), and rounding to the minute makes two genuinely different
// instants read as identical. `formatTime` (minute precision) stays as-is
// for the axis tick labels, which have no room for seconds and don't need
// them at hour-scale.
function formatMs(ms: number): string {
  return format(new Date(ms), "h:mm:ss a");
}

function formatDuration(durationMs: number): string {
  const totalSec = Math.max(0, Math.floor(durationMs / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

// ── Punch method row ("Check-in via  Mobile · Chrome") ─────────────────────────

const PunchMethodRow = ({
  label,
  source,
}: {
  label: string;
  source: PunchSource;
}) => {
  const { t } = useTranslation("common");
  let Icon: LucideIcon;
  let kind: string;
  let detail: string | null;
  if (source.terminal) {
    Icon = Fingerprint;
    kind = t("shared_ui.device_kind_biometric");
    detail = source.terminal.name;
  } else if (source.device) {
    const meta = deviceMeta(source.device, t);
    Icon = meta.Icon;
    kind = meta.kind;
    detail = meta.primary;
  } else if (source.updatedBy) {
    Icon = PenLine;
    kind = t("shared_ui.device_kind_manual");
    detail = formatPerson(source.updatedBy, "") || null;
  } else {
    return null;
  }
  return (
    <div className="flex justify-between gap-3 text-muted-foreground">
      <span>{label}</span>
      <span className="flex items-center gap-1 min-w-0 font-medium text-foreground">
        <Icon size={11} className="shrink-0 text-muted-foreground" />
        <span className="truncate max-w-[160px]">
          {detail && detail !== kind ? `${kind} · ${detail}` : kind}
        </span>
      </span>
    </div>
  );
};

// ── Segment tooltip ───────────────────────────────────────────────────────────

const SegmentTooltip = ({ seg }: { seg: TimelineSegment }) => {
  const { t } = useTranslation("common");
  const isLiveActivity = seg.kind === "activity" && seg.isLive;
  const isLiveWork = seg.kind === "active";
  const isLive = isLiveActivity || isLiveWork;

  return (
    <div className="flex flex-col gap-1 text-[11px] min-w-[140px]">
      {/* Header — icon + name for activities, plain label otherwise */}
      {seg.kind === "activity" ? (
        <div className="flex items-center justify-between gap-2 pb-1 border-b border-border">
          <div className="flex items-center gap-1.5 min-w-0">
            <DynamicIcon
              name={seg.activityIcon}
              size={14}
              style={{ color: seg.cssColor }}
            />
            <span className="font-semibold text-gray-900 truncate">
              {seg.activityName}
            </span>
          </div>
          {isLiveActivity && (
            <span className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-red-600">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              {t("shared_ui.live")}
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 pb-1 border-b border-border">
          <span className="font-semibold capitalize text-gray-900">
            {seg.label}
          </span>
          {isLiveWork && (
            <span className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-red-600">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              {t("shared_ui.live")}
            </span>
          )}
        </div>
      )}
      <div className="flex justify-between gap-3 text-gray-500">
        <span>{t("shared_ui.start")}</span>
        <span className="font-medium text-gray-900">
          {formatMs(seg.startMs)}
        </span>
      </div>
      <div className="flex justify-between gap-3 text-gray-500">
        <span>{isLive ? t("shared_ui.now") : t("shared_ui.end")}</span>
        <span className="font-medium text-gray-900">{formatMs(seg.endMs)}</span>
      </div>
      <div className="flex justify-between gap-3 text-gray-500">
        <span>{t("shared_ui.duration")}</span>
        <span className="font-medium text-gray-900">
          {formatDuration(seg.durationMs)}
        </span>
      </div>
      {(seg.kind === "work" || seg.kind === "active") && (
        <>
          <PunchMethodRow
            label={t("shared_ui.check_in_via")}
            source={seg.checkIn}
          />
          {seg.checkOut && (
            <PunchMethodRow
              label={t("shared_ui.check_out_via")}
              source={seg.checkOut}
            />
          )}
        </>
      )}
    </div>
  );
};

// ── Pending-overlay tooltip — same Start/Now/Duration layout as a real
// segment, so hovering a pending stripe reads exactly like a confirmed one,
// with a "Pending" badge in place of "Live" and a footnote explaining the
// caveat unique to this stripe (won't count until approved). ─────────────────

const PendingOverlayTooltip = ({
  overlay,
}: {
  overlay: PendingOverlaySegment;
}) => {
  const { t } = useTranslation("common");
  return (
    <div className="flex flex-col gap-1 text-[11px] min-w-[160px]">
      <div className="flex items-center justify-between gap-2 pb-1 border-b border-border">
        <span className="font-semibold capitalize text-gray-900">
          {overlay.label}
        </span>
        <span className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-amber-600">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          {t("status.pending")}
        </span>
      </div>
      <div className="flex justify-between gap-3 text-gray-500">
        <span>{t("shared_ui.start")}</span>
        <span className="font-medium text-gray-900">
          {formatMs(overlay.startMs)}
        </span>
      </div>
      <div className="flex justify-between gap-3 text-gray-500">
        <span>{overlay.isLive ? t("shared_ui.now") : t("shared_ui.end")}</span>
        <span className="font-medium text-gray-900">
          {formatMs(overlay.endMs)}
        </span>
      </div>
      <div className="flex justify-between gap-3 text-gray-500">
        <span>{t("shared_ui.duration")}</span>
        <span className="font-medium text-gray-900">
          {formatDuration(overlay.durationMs)}
        </span>
      </div>
      <p className="pt-1 border-t border-border text-gray-500 leading-snug">
        {t("shared_ui.awaiting_hr_approval_hours_hint")}
      </p>
    </div>
  );
};

// ── Single full-height segment ────────────────────────────────────────────────

const SegmentBlock = ({
  seg,
  compact,
}: {
  seg: TimelineSegment;
  compact?: boolean;
}) => {
  const isActive = seg.kind === "active";
  const isRemaining = seg.kind === "remaining";
  const isActivity = seg.kind === "activity";
  const isGap = seg.kind === "gap";
  const isLiveActivity = isActivity && seg.isLive;
  const showLiveAnim = isActive || isLiveActivity;

  return (
    <TooltipProvider delayDuration={120}>
      <RadixTooltip>
        <TooltipTrigger asChild>
          <div
            className="absolute top-0 bottom-0 cursor-pointer group"
            style={{
              // A time axis reads in the text direction, so offsets are measured
              // from the inline start — `left` would run the day backwards in RTL.
              insetInlineStart: `${seg.leftPercent}%`,
              width: `${seg.widthPercent}%`,
              // A quick activity or an unaccounted gap logged for only a
              // minute or two rounds to a sliver of the day's width —
              // invisible, and its icon pill has nowhere to sit. 20px keeps
              // it a real, hoverable block; other segment kinds
              // (late/remaining/etc.) stay at a thin 2px since they carry no
              // icon and a wide floor would misrepresent a genuinely short
              // late arrival.
              minWidth: isActivity || isGap ? "20px" : "2px",
            }}
          >
            {/* Light tint fill — full height */}
            <div
              className="absolute inset-0"
              style={{
                backgroundColor: seg.cssColor,
                opacity: isRemaining ? 0.18 : 0.12,
              }}
            />

            {/* Hover glow overlay */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-[0.14] transition-opacity duration-200"
              style={{ backgroundColor: seg.cssColor }}
            />

            {/* Live: meter-reading sweep — grows along the reading direction. */}
            {showLiveAnim && (
              <div className="absolute inset-0 overflow-hidden">
                <div
                  className="absolute top-0 bottom-0 start-0 animate-grow"
                  style={{ backgroundColor: seg.cssColor, opacity: 0.22 }}
                />
              </div>
            )}

            {/* Thin bar — always visible */}
            {showLiveAnim ? (
              /* Live session: marquee moving stripes */
              <div
                className="absolute inset-x-0 h-[3px] animate-marquee bg-repeat-x"
                style={{
                  top: "50%",
                  transform: "translateY(-50%) skewX(30deg)",
                  backgroundImage: `repeating-linear-gradient(to right, ${seg.cssColor} 0px, ${seg.cssColor} 10px, transparent 10px, transparent 14px)`,
                  backgroundSize: "14px 3px",
                }}
              />
            ) : (
              <div
                className="absolute inset-x-0 h-[3px] group-hover:h-[5px] transition-[height] duration-200"
                style={{
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: seg.cssColor,
                  opacity: isRemaining ? 0.35 : 1,
                }}
              />
            )}

            {/* Activity / unaccounted-gap icon pill — hidden in compact mode */}
            {(isActivity || isGap) && !compact && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="relative flex items-center justify-center w-[22px] h-[22px] rounded-full shrink-0 shadow-xs overflow-hidden bg-white group-hover:scale-110 transition-transform duration-200"
                  style={{ border: `1.5px solid ${seg.cssColor}` }}
                >
                  {/* Light tinted background over white */}
                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor: seg.cssColor, opacity: 0.18 }}
                  />
                  {/* Icon */}
                  {isActivity ? (
                    <DynamicIcon
                      name={seg.activityIcon}
                      size={12}
                      className="relative"
                      style={{
                        color: seg.cssColor,
                        filter: "brightness(0.65)",
                      }}
                    />
                  ) : (
                    <TriangleAlert
                      size={12}
                      className="relative"
                      style={{
                        color: seg.cssColor,
                        filter: "brightness(0.85)",
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </TooltipTrigger>

        <TooltipContent
          side="top"
          className="rounded-sm bg-white text-gray-900 border-border shadow-lg"
        >
          <SegmentTooltip seg={seg} />
        </TooltipContent>
      </RadixTooltip>
    </TooltipProvider>
  );
};

// ── Time markers ──────────────────────────────────────────────────────────────

interface TimeMarker {
  label: string;
  leftPercent: number;
}

function buildMarkers(
  shiftStartMs: number,
  shiftEndMs: number,
  shiftDurationMs: number,
): TimeMarker[] {
  const markers: TimeMarker[] = [];
  const STEP = 3_600_000; // 1 h
  // Below this gap to the window end, an hourly tick reads as crammed against
  // the forced end tick (e.g. window extended to "now" past shift end by a
  // pending exception) — drop it and let the end tick stand alone.
  const MIN_GAP_MS = STEP * 0.25;

  // Step from shift start in 1-hour increments up to (but not past) shift end
  let t = shiftStartMs;
  while (t < shiftEndMs) {
    if (shiftEndMs - t >= MIN_GAP_MS) {
      const leftPercent = ((t - shiftStartMs) / shiftDurationMs) * 100;
      const d = new Date(t);
      const label = d.getMinutes() === 0 ? formatTime(d) : formatTime(d);
      markers.push({ label, leftPercent });
    }
    t += STEP;
  }

  // Always include shift end
  markers.push({
    label: formatTime(new Date(shiftEndMs)),
    leftPercent: 100,
  });

  return markers;
}

// ── AttendanceTimeline ────────────────────────────────────────────────────────

export const AttendanceTimeline = ({
  result,
  compact = false,
  barOnly = false,
  isWeekend = false,
  isAbsent = false,
  className,
  pendingOverlaySegments = [],
}: AttendanceTimelineProps) => {
  const { t, i18n } = useTranslation("common");
  const { segments, shiftStartMs, shiftEndMs, shiftDurationMs } = result;

  // `buildMarkers` formats its labels through date-fns, which reads the global
  // locale — so the language has to be a dependency or the axis keeps rendering
  // the previous language's AM/PM (e.g. "9:00 ص" after switching to English).
  const markers = useMemo(
    () => buildMarkers(shiftStartMs, shiftEndMs, shiftDurationMs),
    [shiftStartMs, shiftEndMs, shiftDurationMs, i18n.language],
  );

  // Split so floored (activity/gap) segments can render in their own later
  // pass — see the comment where they're mapped below.
  const baseSegments = useMemo(
    () => segments.filter((s) => s.kind !== "activity" && s.kind !== "gap"),
    [segments],
  );
  const flooredSegments = useMemo(
    () => segments.filter((s) => s.kind === "activity" || s.kind === "gap"),
    [segments],
  );

  const hasWork = segments.some(
    (s) => s.kind === "work" || s.kind === "active" || s.kind === "activity",
  );

  const hideExtras = compact || barOnly;
  const minH = barOnly ? "" : compact ? "min-h-[36px]" : "min-h-[64px]";
  const fixedH = barOnly ? "h-2" : "";

  // ── Special empty states ──────────────────────────────────────────────────

  const minW = compact || barOnly ? "min-w-0" : "min-w-[200px]";

  // A pending clock attempt means the day isn't truly flat — fall through to
  // the full timeline so its overlay stripe still renders instead of being
  // silently hidden behind the plain weekend/absent tint.
  const hasPending = pendingOverlaySegments.length > 0;

  if (!hasWork && isWeekend && !hasPending) {
    return (
      <div
        className={cn(
          "relative h-full overflow-hidden",
          minW,
          minH,
          fixedH,
          className,
        )}
      >
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "var(--color-warning)", opacity: 0.08 }}
        />
        <div
          className="absolute inset-x-0 h-[3px]"
          style={{
            top: "50%",
            transform: "translateY(-50%)",
            backgroundColor: "var(--color-warning)",
            opacity: 0.4,
          }}
        />
      </div>
    );
  }

  if (!hasWork && isAbsent && !hasPending) {
    return (
      <div
        className={cn(
          "relative h-full overflow-hidden",
          minW,
          minH,
          fixedH,
          className,
        )}
      >
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "var(--color-danger)", opacity: 0.08 }}
        />
        <div
          className="absolute inset-x-0 h-[3px]"
          style={{
            top: "50%",
            transform: "translateY(-50%)",
            backgroundColor: "var(--color-danger)",
            opacity: 0.35,
          }}
        />
      </div>
    );
  }

  // ── Full timeline ─────────────────────────────────────────────────────────

  return (
    <div
      className={cn(
        "relative h-full overflow-hidden select-none",
        minW,
        minH,
        fixedH,
        className,
      )}
      role="img"
      aria-label={t("shared_ui.attendance_timeline")}
    >
      {/* ── Segment blocks (full height, light tint + thin bar) ───────────── */}
      {/* Floored blocks (activity/gap — see SegmentBlock's minWidth) render
          in a SEPARATE, later pass so they paint on top of their neighbours.
          A minWidth floor can make a genuinely few-second segment's box
          wider on screen than its real time span, encroaching into an
          adjacent segment's geometric area — without this split that
          neighbour (drawn later in plain array order) would cover the
          overlap and steal its hover, making the floored block impossible
          to hover exactly where it's most likely to be tiny. */}
      {baseSegments.map((seg) => (
        <SegmentBlock
          key={`${seg.kind}-${seg.startMs}`}
          seg={seg}
          compact={hideExtras}
        />
      ))}
      {flooredSegments.map((seg) => (
        <SegmentBlock
          key={`${seg.kind}-${seg.startMs}`}
          seg={seg}
          compact={hideExtras}
        />
      ))}

      {/* ── Pending-exception overlay — diagonal stripe, not a base segment ── */}
      {pendingOverlaySegments.map((overlay) => (
        <TooltipProvider key={`pending-${overlay.id}`} delayDuration={120}>
          <RadixTooltip>
            <TooltipTrigger asChild>
              <div
                className="absolute top-0 bottom-0 pointer-events-auto"
                style={{
                  left: `${overlay.leftPercent}%`,
                  width: `${overlay.widthPercent}%`,
                  minWidth: "3px",
                  backgroundImage: `repeating-linear-gradient(45deg, ${overlay.cssColor} 0, ${overlay.cssColor} 3px, transparent 3px, transparent 7px)`,
                  opacity: 0.55,
                }}
              />
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="rounded-sm bg-white text-gray-900 border-border shadow-lg"
            >
              <PendingOverlayTooltip overlay={overlay} />
            </TooltipContent>
          </RadixTooltip>
        </TooltipProvider>
      ))}

      {/* ── Time markers — hidden in compact/barOnly mode ─────────────────── */}
      {!hideExtras && (
        <div className="absolute bottom-0 inset-x-0 h-[18px] pointer-events-none">
          {markers.map((m, i) => {
            const isFirst = i === 0;
            const isLast = i === markers.length - 1;
            const xShift = isFirst ? "0%" : isLast ? "-100%" : "-50%";
            // On mobile show only first, last, and every 3rd marker to prevent overlap
            const visibleOnMobile = isFirst || isLast || i % 3 === 0;
            return (
              <div
                key={m.label}
                className={cn(
                  "absolute flex flex-col items-start",
                  !visibleOnMobile && "hidden sm:flex",
                )}
                style={{
                  insetInlineStart: `${m.leftPercent}%`,
                  // translateX is physical, so the nudge that keeps the first and
                  // last labels inside the track has to invert under RTL.
                  transform: `translateX(calc(${xShift} * var(--rtl-sign, 1)))`,
                }}
              >
                <div className="w-px h-1 bg-gray-400/40" />
                <span className="text-[8px] text-gray-500/80 leading-tight whitespace-nowrap font-medium">
                  {m.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
